import { useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useLiveFeed, type SpreadData } from "@/hooks/use-live-feed";
import { ChevronDown, Search } from "@/components/ui/icons";
import {
  futuresSymbolMeta,
  type FuturesSymbolCategory,
} from "@/lib/futures-symbols";
import { cn } from "@/lib/utils";

/**
 * Terminal watchlist. Rows come from the live broker-radar socket.io
 * feed keyed by canonical symbol. Clicking a row bubbles the symbol up
 * to the parent, which drives the chart + order panel.
 *
 * The list of symbols shown is the pair's `symbolAllowlist` — what the
 * operator has authorized for this account pair. If broker-radar
 * doesn't cover a symbol the row shows "—" for bid/ask and is
 * click-disabled (you can't trade a symbol we can't price).
 *
 * No JS polling / intervals / animation loops in here. `useLiveFeed`
 * runs a single shared WS connection per mount — one price update
 * triggers one React re-render for the whole watchlist.
 */
export function Watchlist({
  allowlist,
  selected,
  onSelect,
  allowUnpricedSelection = false,
  mode = "standard",
}: {
  allowlist: string[];
  selected: string;
  onSelect(symbol: string): void;
  allowUnpricedSelection?: boolean;
  mode?: "standard" | "fx-cfd" | "futures";
}): ReactNode {
  const { data, isConnected } = useLiveFeed("opofinance", "ecn");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const compact = mode !== "standard";

  const bySymbol = useMemo(() => {
    const m = new Map<string, SpreadData>();
    for (const item of data) m.set(item.symbol.toUpperCase(), item);
    return m;
  }, [data]);

  const normalizedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allowlist
      .map((rawSym) => {
        const sym = rawSym.toUpperCase();
        const meta = mode === "futures" ? futuresSymbolMeta(sym) : null;
        return {
          sym,
          label: meta?.label ?? sym,
          category:
            meta?.category ??
            (mode === "fx-cfd"
              ? "FX & CFD"
              : ("Symbols" as FuturesSymbolCategory | "FX & CFD" | "Symbols")),
          tick: bySymbol.get(sym),
        };
      })
      .filter((row) => {
        if (!q) return true;
        return (
          row.sym.toLowerCase().includes(q) ||
          row.label.toLowerCase().includes(q) ||
          row.category.toLowerCase().includes(q)
        );
      });
  }, [allowlist, bySymbol, mode, query]);

  const groupedRows = useMemo(() => {
    const groups = new Map<string, typeof normalizedRows>();
    for (const row of normalizedRows) {
      const prev = groups.get(row.category) ?? [];
      prev.push(row);
      groups.set(row.category, prev);
    }
    return Array.from(groups.entries());
  }, [normalizedRows]);

  const toggleGroup = (group: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card/60">
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b border-border/60",
          compact ? "px-2.5 py-1.5" : "px-3 py-2",
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
            Watchlist
          </span>
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              isConnected ? "bg-emerald-500" : "bg-amber-500",
            )}
          />
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">
          {normalizedRows.length}/{allowlist.length}
        </span>
      </div>

      {compact && (
        <div className="border-b border-border/60 px-2 py-1.5">
          <label className="relative block">
            <span className="sr-only">Search symbols</span>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Symbol"
              autoComplete="off"
              spellCheck={false}
              aria-label="Search symbols"
              className="h-7 w-full rounded-md border border-border bg-background pl-8 pr-2 font-mono text-xs outline-none transition-colors focus:border-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/60"
            />
          </label>
        </div>
      )}

      <div className={cn("flex-1 overflow-y-auto", mode === "futures" && "max-h-[276px]")}>
        {allowlist.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            No symbols in this pair’s allowlist. Add one from the
            account-pair page to trade it here.
          </div>
        ) : groupedRows.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            No Symbols Match
          </div>
        ) : (
          groupedRows.map(([group, rows]) => {
            const isCollapsed = collapsed.has(group);
            return (
              <div key={group}>
                {compact && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group)}
                    className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-border/40 bg-card/95 px-2.5 py-1 text-left backdrop-blur"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      {group}
                    </span>
                    <span className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                      {rows.length}
                      <ChevronDown
                        aria-hidden="true"
                        className={cn("size-3 transition-transform", isCollapsed && "-rotate-90")}
                      />
                    </span>
                  </button>
                )}
                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.ul
                      initial={compact ? { opacity: 0, height: 0 } : false}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      {rows.map((row) => (
                        <WatchlistRow
                          key={row.sym}
                          symbol={row.sym}
                          label={row.label}
                          tick={row.tick}
                          selected={row.sym === selected}
                          selectable={Boolean(row.tick?.isOpen) || allowUnpricedSelection}
                          dense={compact}
                          onSelect={onSelect}
                        />
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function WatchlistRow({
  symbol,
  label,
  tick,
  selected,
  selectable,
  dense = false,
  onSelect,
}: {
  symbol: string;
  label: string;
  tick: SpreadData | undefined;
  selected: boolean;
  selectable: boolean;
  dense?: boolean;
  onSelect(symbol: string): void;
}): ReactNode {
  return (
    <li>
      <motion.button
        type="button"
        whileTap={{ scale: 0.99 }}
        onClick={() => selectable && onSelect(symbol)}
        disabled={!selectable}
        className={cn(
          "flex w-full items-center justify-between border-b border-border/40 text-left transition-colors",
          dense ? "min-h-11 gap-2 px-2.5 py-1.5" : "min-h-14 gap-3 px-3 py-2",
          selected ? "bg-accent/15 text-foreground" : "hover:bg-muted/40",
          !selectable && "opacity-50",
        )}
      >
        <span className="min-w-0 flex flex-col">
          <span className="font-mono text-xs font-medium text-foreground">
            {symbol}
          </span>
          <span className="truncate text-[10px] text-muted-foreground">
            {tick ? `spread ${tick.spread_points.toFixed(1)}` : label}
          </span>
        </span>
        <span className="text-right">
          {tick && tick.isOpen ? (
            <>
              <span
                className={cn(
                    "block font-mono tabular-nums",
                    dense ? "text-xs" : "text-sm",
                  tick.direction === "up"
                    ? "text-emerald-500"
                    : tick.direction === "down"
                      ? "text-rose-500"
                      : "text-foreground",
                )}
              >
                {tick.bid.toFixed(tick.digits)}
              </span>
              {tick.change24h && (
                <span
                  className={cn(
                    "block font-mono text-[10px] tabular-nums",
                    tick.change24h.startsWith("+")
                      ? "text-emerald-500/70"
                      : tick.change24h.startsWith("-")
                        ? "text-rose-500/70"
                        : "text-muted-foreground",
                  )}
                >
                  {tick.change24h}
                </span>
              )}
            </>
          ) : (
            <span className="block font-mono text-sm text-muted-foreground">
              —
            </span>
          )}
        </span>
      </motion.button>
    </li>
  );
}
